import React from 'react';
import { ArrowLeft, FileText, CheckCircle, XCircle, AlertTriangle, Scale, ChevronRight } from 'lucide-react';
import { useApp } from './AppContext';
import type { Screen } from '../App';

interface TermsScreenProps {
  onNavigate: (screen: Screen) => void;
  onBack: () => void;
}

export const TermsScreen: React.FC<TermsScreenProps> = ({ onNavigate, onBack }) => {
  const { getBackgroundClass } = useApp();

  return (
    <div className={`screen-min-h-full ${getBackgroundClass()} max-w-full lg:max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto flex flex-col`}>
      {/* Header */}
      <div className="app-header-fixed flex items-center">
        <button 
          onClick={onBack} 
          className="mr-2 sm:mr-3 arrow-button flex-shrink-0"
          aria-label="Назад"
        >
          <ArrowLeft className="arrow-icon" />
        </button>
        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
          <FileText className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 glass-icon flex-shrink-0" />
          <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl landscape-compact-text-xs landscape:text-sm font-semibold glass-text truncate">
            Пользовательское соглашение
          </h1>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="app-content-scrollable">
        <div className="glass-panel rounded-xl p-4 sm:p-6 md:p-8">
          
          {/* Introduction */}
          <div className="mb-6 sm:mb-8">
            <p className="glass-text text-sm sm:text-base leading-relaxed">
              Настоящее Пользовательское соглашение (далее — «Соглашение») регулирует отношения между пользователем 
              (далее — «Пользователь») и платформой ВОХР ТЕСТ (далее — «Сервис») при использовании веб-приложения 
              для тестирования сотрудников ведомственной охраны.
            </p>
            <p className="glass-text-secondary text-xs sm:text-sm mt-3">
              Дата вступления в силу: {new Date().toLocaleDateString('ru-RU', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>

          {/* Section 1: Acceptance of Terms */}
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 glass-icon flex-shrink-0" />
              <h2 className="text-base sm:text-lg md:text-xl font-semibold glass-text">1. Принятие условий</h2>
            </div>
            <div className="space-y-3 glass-text-secondary text-sm sm:text-base">
              <p className="leading-relaxed">
                Используя Сервис, вы подтверждаете, что:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Прочитали, поняли и согласны с настоящим Соглашением</li>
                <li>Достигли возраста 18 лет или используете Сервис под контролем родителей/опекунов</li>
                <li>Обязуетесь соблюдать все условия использования Сервиса</li>
              </ul>
              <p className="glass-panel p-3 rounded-lg mt-4 leading-relaxed">
                <strong className="glass-text">Важно:</strong> Если вы не согласны с условиями, пожалуйста, прекратите использование Сервиса.
              </p>
            </div>
          </div>

          {/* Section 2: Service Description */}
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="h-5 w-5 sm:h-6 sm:w-6 glass-icon flex-shrink-0" />
              <h2 className="text-base sm:text-lg md:text-xl font-semibold glass-text">2. Описание Сервиса</h2>
            </div>
            <div className="space-y-3 glass-text-secondary text-sm sm:text-base">
              <p className="leading-relaxed">ВОХР ТЕСТ — это бесплатная образовательная платформа, предоставляющая:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Тестовые вопросы по различным категориям (пожарная подготовка, первая помощь, правовая подготовка и др.)</li>
                <li>Режимы подготовки, экзамена и марафона</li>
                <li>Статистику прохождения тестов</li>
                <li>Нормативные документы для изучения</li>
              </ul>
              <p className="leading-relaxed mt-3">
                Сервис предназначен исключительно для <strong className="glass-text">образовательных целей</strong> и подготовки 
                к профессиональным экзаменам.
              </p>
            </div>
          </div>

          {/* Section 3: User Responsibilities */}
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Scale className="h-5 w-5 sm:h-6 sm:w-6 glass-icon flex-shrink-0" />
              <h2 className="text-base sm:text-lg md:text-xl font-semibold glass-text">3. Обязанности Пользователя</h2>
            </div>
            <div className="space-y-3 glass-text-secondary text-sm sm:text-base">
              <p className="leading-relaxed">Пользователь обязуется:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Использовать Сервис в законных целях и в соответствии с действующим законодательством</li>
                <li>Не копировать, не распространять и не публиковать материалы Сервиса без разрешения</li>
                <li>Не предпринимать действий, которые могут нарушить работу Сервиса</li>
                <li>Не использовать автоматизированные системы (боты) для доступа к Сервису</li>
              </ul>
            </div>
          </div>

          {/* Section 4: Intellectual Property */}
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="h-5 w-5 sm:h-6 sm:w-6 glass-icon flex-shrink-0" />
              <h2 className="text-base sm:text-lg md:text-xl font-semibold glass-text">4. Интеллектуальная собственность</h2>
            </div>
            <div className="space-y-3 glass-text-secondary text-sm sm:text-base">
              <p className="leading-relaxed">
                Все материалы Сервиса (текстовые вопросы, дизайн, программный код, логотипы) являются 
                интеллектуальной собственностью и защищены авторским правом.
              </p>
              <p className="leading-relaxed mt-3">
                Пользователь получает ограниченное право на использование Сервиса исключительно для 
                личного обучения и подготовки к экзаменам.
              </p>
            </div>
          </div>

          {/* Section 5: Disclaimers */}
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6 glass-icon flex-shrink-0" />
              <h2 className="text-base sm:text-lg md:text-xl font-semibold glass-text">5. Ограничение ответственности</h2>
            </div>
            <div className="space-y-3 glass-text-secondary text-sm sm:text-base">
              <p className="leading-relaxed">Сервис предоставляется «как есть», без каких-либо гарантий:</p>
              <ul className="list-disc list-inside space-y-2 ml-2">
                <li>Мы не гарантируем 100% точность и актуальность всех тестовых вопросов</li>
                <li>Мы не несем ответственности за результаты реальных экзаменов после подготовки в Сервисе</li>
                <li>Сервис может быть временно недоступен из-за технических работ или обновлений</li>
                <li>Мы не несем ответственности за потерю данных, если вы очистили кеш браузера</li>
              </ul>
              <p className="glass-panel p-3 rounded-lg mt-4 leading-relaxed">
                <strong className="glass-text">Важно:</strong> Материалы Сервиса являются дополнительными и не заменяют 
                официальную подготовку и инструктаж.
              </p>
            </div>
          </div>

          {/* Section 6: Advertising */}
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="h-5 w-5 sm:h-6 sm:w-6 glass-icon flex-shrink-0" />
              <h2 className="text-base sm:text-lg md:text-xl font-semibold glass-text">6. Реклама</h2>
            </div>
            <div className="space-y-3 glass-text-secondary text-sm sm:text-base">
              <p className="leading-relaxed">
                Сервис может содержать рекламные материалы от сторонних рекламных сетей (Яндекс.РСЯ). 
                Мы не несем ответственности за содержание рекламы и товары/услуги рекламодателей.
              </p>
            </div>
          </div>

          {/* Section 7: Termination */}
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center gap-3 mb-4">
              <XCircle className="h-5 w-5 sm:h-6 sm:w-6 glass-icon flex-shrink-0" />
              <h2 className="text-base sm:text-lg md:text-xl font-semibold glass-text">7. Прекращение доступа</h2>
            </div>
            <div className="space-y-3 glass-text-secondary text-sm sm:text-base">
              <p className="leading-relaxed">
                Мы оставляем за собой право ограничить или прекратить доступ к Сервису без объяснения причин 
                в случае нарушения условий настоящего Соглашения.
              </p>
            </div>
          </div>

          {/* Section 8: Changes to Terms */}
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="h-5 w-5 sm:h-6 sm:w-6 glass-icon flex-shrink-0" />
              <h2 className="text-base sm:text-lg md:text-xl font-semibold glass-text">8. Изменения в Соглашении</h2>
            </div>
            <div className="space-y-3 glass-text-secondary text-sm sm:text-base">
              <p className="leading-relaxed">
                Мы можем изменять условия настоящего Соглашения в любое время. Все изменения вступают в силу 
                с момента их публикации на данной странице. Продолжая использовать Сервис после изменений, 
                вы соглашаетесь с новыми условиями.
              </p>
            </div>
          </div>

          {/* Section 9: Governing Law */}
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Scale className="h-5 w-5 sm:h-6 sm:w-6 glass-icon flex-shrink-0" />
              <h2 className="text-base sm:text-lg md:text-xl font-semibold glass-text">9. Применимое право</h2>
            </div>
            <div className="space-y-3 glass-text-secondary text-sm sm:text-base">
              <p className="leading-relaxed">
                Настоящее Соглашение регулируется законодательством Российской Федерации. 
                Все споры решаются в соответствии с действующим законодательством РФ.
              </p>
            </div>
          </div>

          {/* Contact Information */}
          <button 
            onClick={() => onNavigate('contacts')}
            className="glass-panel rounded-lg p-4 sm:p-6 bg-white/5 glass-interactive cursor-pointer w-full text-left flex items-start justify-between group"
          >
            <div>
              <h3 className="text-base sm:text-lg font-semibold glass-text mb-3">Контакты</h3>
              <p className="glass-text-secondary text-sm sm:text-base leading-relaxed">
                По вопросам, связанным с настоящим Соглашением, обращайтесь через раздел Контакты.
              </p>
            </div>
            <ChevronRight className="h-5 w-5 glass-icon flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity mt-1" />
          </button>

        </div>
      </div>
    </div>
  );
};

